import type { BlockedVisitorSummary, MembershipSummary } from '@livechat/shared';
import { useEffect, useState } from 'react';

import { listBlockedVisitors, unblockVisitor } from '../api.js';
import { useDateFormat } from '../date-format.js';

export function TenantBlockedVisitors({ membership }: { membership: MembershipSummary }) {
	const { formatDateTime } = useDateFormat();
	const [blockedVisitors, setBlockedVisitors] = useState<BlockedVisitorSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [busyId, setBusyId] = useState<string>();
	const [error, setError] = useState<string>();

	useEffect(() => {
		let current = true;
		setLoading(true);
		setError(undefined);
		void listBlockedVisitors(membership.tenantId)
			.then((items) => {
				if (current) setBlockedVisitors(items);
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load blocked visitors',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [membership.tenantId]);

	async function unblock(visitor: BlockedVisitorSummary): Promise<void> {
		if (!window.confirm(`Unblock ${visitor.name ?? 'this visitor'}?`)) return;
		setBusyId(visitor.id);
		setError(undefined);
		try {
			await unblockVisitor(membership.tenantId, visitor.id);
			setBlockedVisitors((current) => current.filter((item) => item.id !== visitor.id));
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not unblock the visitor');
		} finally {
			setBusyId(undefined);
		}
	}

	return (
		<div className="blocked-visitors-page">
			<div className="subsection-heading">
				<div>
					<h3>Blocked Visitors</h3>
					<p>Review visitor identity and IP blocks created from agent conversations.</p>
				</div>
			</div>
			{error && <div className="alert error">{error}</div>}
			{loading ? (
				<p>Loading blocked visitors…</p>
			) : (
				<div className="blocked-visitors-table">
					<table>
						<thead>
							<tr>
								<th scope="col">Visitor</th>
								<th scope="col">Date</th>
								<th scope="col">Block details</th>
								<th scope="col">Agent</th>
								<th aria-label="Actions" scope="col" />
							</tr>
						</thead>
						<tbody>
							{blockedVisitors.map((visitor) => (
								<tr key={visitor.id}>
									<td>
										<span className="blocked-visitor-identity">
											<strong>{visitor.name ?? 'Visitor'}</strong>
											{visitor.company && <span>{visitor.company}</span>}
										</span>
									</td>
									<td>
										<time dateTime={visitor.blockedAt}>
											{formatDateTime(visitor.blockedAt)}
										</time>
									</td>
									<td>
										<span className="blocked-visitor-details">
											<strong>{visitor.blockDetails}</strong>
											<small>
												Visitor {visitor.visitorId}
												{visitor.ipAddress
													? ` · IP ${visitor.ipAddress}`
													: ''}
											</small>
										</span>
									</td>
									<td>{visitor.blockedByName}</td>
									<td>
										<button
											className="secondary-button compact-button"
											disabled={busyId === visitor.id}
											onClick={() => void unblock(visitor)}
											type="button"
										>
											{busyId === visitor.id ? 'Unblocking…' : 'Unblock'}
										</button>
									</td>
								</tr>
							))}
							{blockedVisitors.length === 0 && (
								<tr>
									<td className="empty-configuration" colSpan={5}>
										No visitors are currently blocked.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
